import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { AppCalendar } from '@/components/calendar/app-calendar';
import type {
  CalendarEvent,
  CalendarView,
} from '@/components/calendar/types';
import { RescheduleDialog } from '@/components/reschedule-dialog';
import { useLanguage } from '@/contexts/language-context';
import { updateJobVisit, type BusinessVisitDto } from '@/lib/api/jobs';

import { visitTone } from './helpers';

interface Props {
  visits: BusinessVisitDto[];
  businessId: string;
  date: Date;
  view: CalendarView;
  onNavigate: (next: Date) => void;
  onView: (next: CalendarView) => void;
  onSelectJob: (jobId: string) => void;
  isLoading?: boolean;
}

export function JobsCalendar({
  visits,
  businessId,
  date,
  view,
  onNavigate,
  onView,
  onSelectJob,
  isLoading,
}: Props) {
  const { t } = useLanguage();
  const [pending, setPending] = useState<{
    visitId: string;
    jobId: string;
    label: string;
    from: string;
    to: string;
  } | null>(null);

  // Each chip is a real visit, so — unlike the old job-anchor reschedule —
  // every non-terminal visit is individually draggable, recurring or not.
  const reschedule = useMutation({
    mutationFn: ({
      jobId,
      visitId,
      date: scheduledDate,
    }: {
      jobId: string;
      visitId: string;
      date: string;
    }) => updateJobVisit(businessId, jobId, visitId, { scheduled_date: scheduledDate }),
    meta: { successMessage: t.visitRescheduled },
  });

  const events = useMemo<CalendarEvent[]>(
    () =>
      visits.map((visit) => ({
        id: visit.id,
        date: visit.scheduled_date,
        label: visit.job_title,
        tone: visitTone(visit.status),
        meta: visit,
      })),
    [visits],
  );

  return (
    <div data-testid="jobs-calendar">
      <AppCalendar
        events={events}
        date={date}
        view={view}
        onNavigate={onNavigate}
        onView={onView}
        isLoading={isLoading}
        emptyLabel={t.calendarNoJobsMonth}
        onSelectEvent={(event) => onSelectJob((event.meta as BusinessVisitDto).job_id)}
        draggableAccessor={(event) => {
          const status = (event.meta as BusinessVisitDto).status;
          return status !== 'completed' && status !== 'cancelled';
        }}
        onEventDrop={({ event, date: to }) => {
          const visit = event.meta as BusinessVisitDto;
          setPending({
            visitId: visit.id,
            jobId: visit.job_id,
            label: event.label,
            from: event.date,
            to,
          });
        }}
      />
      {pending && (
        <RescheduleDialog
          title={pending.label}
          currentDate={pending.from}
          targetDate={pending.to}
          isPending={reschedule.isPending}
          onCancel={() => setPending(null)}
          onConfirm={(newISO) =>
            reschedule.mutate(
              { jobId: pending.jobId, visitId: pending.visitId, date: newISO },
              { onSuccess: () => setPending(null) },
            )
          }
        />
      )}
    </div>
  );
}
